import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { Observable } from 'rxjs';

// @angular/fire/firestore next api
import {
  collection as getCollectionRef,
  DocumentData,
  endBefore,
  Firestore,
  getAggregateFromServer,
  getCountFromServer,
  getDocsFromServer,
  limit,
  limitToLast,
  orderBy,
  query,
  Query,
  QueryFilterConstraint,
  startAfter,
  where,
  onSnapshot,
} from '@angular/fire/firestore';

// Models
import {
  Aggregate,
  BatchOperation,
  Condition,
  DataSourceIface,
  Junction,
  JunctionKey,
  OrderBy,
  PageBy,
  PaginateBy,
  QueryConfig,
} from "src/app/v2/models/firestore-interaction.model";
import { Collection, SubCollection } from "src/app/v2/models/collection-reference.model";

// Services
import { FirebaseEmulatorsService } from 'src/app/services/firebase-emulators.service';

@Injectable({
  providedIn: 'root'
})
export class FirestoreService implements DataSourceIface {

  constructor(
    private fs: Firestore,
    private afs: AngularFirestore,
    private fsEmulatorService: FirebaseEmulatorsService,
  ) {
    this.fsEmulatorService.useEmulators({ afFirestore: this.afs, firestore: this.fs })
  }

  /**
   * batchReader retrieve all docs for a given set of QueryConfig
   *
   * do NOT use, keep it for now.
   * originally built to address getting docs without hitting firestore limits
   * however it doesn't completely fit the needs.
   * @param collection
   * @param configs
   * @param orderBy
   * @returns
   */
  private async batchReader(collection: Collection, configs: QueryConfig[], orderBy?: OrderBy[]) {
    const retrievedDocuments = [];  // initialize an empty array to store all retrieved documents
    const limit = 25;  // number of documents to retrieve per batch
    let lastDocument = null;  // keep track of the last document for pagination
    let currentRecordsReturned = 0;  // total number of records returned so far

    try {
      // Continuously query Firestore in batches of the defined limit until no more documents are found
      while (true) { // eslint-disable-line no-constant-condition

        const paginate: PaginateBy = {
          limit,
        };

        if (lastDocument) { // If lastDoc found, we have to reference it's ID for the next pagination
          paginate.startAfter = lastDocument.id; // Set startAfter if we have a previous document
        }

        // Fetch the next batch of documents
        const batchOfQueriedDocuments = await this.getCollectionPromise(collection, configs, orderBy, paginate);

        // If no more documents are found, exit the loop
        if (!batchOfQueriedDocuments || batchOfQueriedDocuments.length === 0) {
          console.info('no more documents to retrieve. Exiting loop.');
          break;
        }

        // Add the retrieved batch to the total collection of product groups
        retrievedDocuments.push(...batchOfQueriedDocuments);
        currentRecordsReturned += batchOfQueriedDocuments.length;

        // Update the last document for pagination
        lastDocument = batchOfQueriedDocuments[batchOfQueriedDocuments.length - 1];

        // Log the progress
        console.log(`retrieved batch of ${batchOfQueriedDocuments.length} documents, total so far: ${currentRecordsReturned}`);

        // If the number of returned documents is less than the limit, assume end of collection
        if (batchOfQueriedDocuments.length < limit) {
          console.info('final batch retrieved, less than limit. No more documents to query.');
          break;
        }
      }

      // Return all accumulated documents
      console.log(`total docs retrieved: ${currentRecordsReturned}`);
      return retrievedDocuments;
    } catch (error) {
      console.error('error retrieving documents:', error);
      throw new Error('failed to retrieve documents. Please check your query or network connection.');
    }
  }

  private _buildQuery(q: Query, configs: QueryConfig[]): Query {
    configs.forEach(item => {
      if (JunctionKey in item) {
        const junc = item as Junction
        q = query(q, junc.fn(...this._join(junc.conditions)))
      } else {
        const cond = item as Condition
        q = query(q, where(cond.field, cond.operator, cond.value))
      }
    })
    return q
  }

  /**
   * count will get total documents that match query conditions
   *
   * @param collection firestore document collection to query
   * @param configs array of where clause query details to filter by
   * @returns Promise<number>
   */
  public async count(collection: Collection, configs: QueryConfig[]): Promise<number> {
    let total: number = 0;
    try {
      const response = await this.getCollectionAggregate(
        collection,
        configs,
        { count: true },
      )
      total = response.count
    } catch (err) {
      console.error(`count(${collection}, ..) failure,`, err, { configs })
    }

    console.debug(`count(${collection}, ..)`, { count: total, configs })
    return total
  }

  /**
   * find is an alias to getCollection, also see DataSourceIface
   *
   * @param collection firestore document collection to query
   * @param configs array of where clause query details to filter by
   * @param sort optional, order to in which to sort results by
   * @param page optional, results page size and reference point
   * @returns Observable[]
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public find(collection: Collection, configs: QueryConfig[], sort?: OrderBy[], page?: PaginateBy): Observable<any[]> {
    return this.getCollectionObservable(collection, configs, sort, page)
  }

  public findFromServer(collection: Collection, configs: QueryConfig[], sort?: OrderBy[], page?: PaginateBy): Promise<any[]> {
    return this.getCollectionPromise(collection, configs, sort, page)
  }

  private _buildFinalQuery(
    collection: Collection,
    configs: QueryConfig[] = [],
    sort?: OrderBy[],
    paginate?: PaginateBy
  ): Query {
    const lim = this._limit(paginate);

    let qry = this._buildQuery(
      query(getCollectionRef(this.fs, collection)),
      configs
    );

    if (sort?.length > 0) {
      sort.forEach(order => {
        qry = query(qry, orderBy(order.field, order.direction));
      });
    } else if (paginate?.startAfter || paginate?.endBefore) {
      qry = query(qry, orderBy('__name__', 'asc'));
    }

    if (paginate?.startAfter && lim !== undefined) {
      qry = query(qry, startAfter(paginate.startAfter), limit(lim));
    } else if (paginate?.endBefore && lim !== undefined) {
      qry = query(qry, endBefore(paginate.endBefore), limitToLast(lim));
    } else if (lim !== undefined) {
      qry = query(qry, limit(lim));
    }

    return qry;
  }

  /**
   * getCollectionPromise executes a query for the given firestore document collection
   *
   * @param collection firestore document collection to query
   * @param configs array of where clause query details to filter by
   * @param sort optional, order to in which to sort results by
   * @param paginate optional, results page size and reference point
   * @returns Promise[]
   */

  public getCollectionPromise(
    collection: Collection,
    configs: QueryConfig[] = [],
    sort?: OrderBy[],
    paginate?: PaginateBy
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any[]> {
    const qry = this._buildFinalQuery(collection, configs, sort, paginate);
    return getDocsFromServer(qry).then(snapshot => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return snapshot.docs.map((doc: any) => ({
        ...doc.data(),
        id: doc.id
      }));
    });
  }


  /**
   * getCollectionObservable sets up a real-time stream for the given firestore collection using the provided query configuration.
   * The stream emits an array of documents every time the data changes.
   *
   * @param collection firestore document collection to query
   * @param configs array of where clause query details to filter by
   * @param sort optional, order to in which to sort results by
   * @param paginate optional, results page size and reference point
   * @returns Observable[]
   */

  public getCollectionObservable(
    collection: Collection,
    configs: QueryConfig[] = [],
    sort?: OrderBy[],
    paginate?: PaginateBy
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any[]> {
    const qry = this._buildFinalQuery(collection, configs, sort, paginate);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Observable<any[]>(subscriber => {
      const unsubscribe = onSnapshot(
        qry,
        snapshot => {
          const docs = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          }));
          subscriber.next(docs);
        },
        error => {
          console.error(`onSnapshot error for getCollectionPromise('${collection}')`, error);
          subscriber.error(error);
        }
      );

      return () => unsubscribe();
    });
  }

  /**
   * getCollectionAggregate runs a collection query and then the aggregate
   * function(s) against the results.
   *
   * @param collection firestore document collection to query
   * @param configs array of where clause query details to filter by
   * @param aggregate aggregate spec to use
   * @returns
   *
   * For details on how to use aggregate functions see firestore documentation
   * https://firebase.google.com/docs/firestore/query-data/aggregation-queries#web
   */
  public async getCollectionAggregate(
    collection: Collection,
    configs: QueryConfig[],
    aggregate: Aggregate,
  ): Promise<DocumentData> {
    if (!aggregate.count && !aggregate.spec) {
      throw new Error('missing required arggregate spec, must have count: true or AggregateSpec from firebase/firestore')
    }

    const q = this._buildQuery(
      query(getCollectionRef(this.fs, collection)),
      configs
    )

    let snapshot: DocumentData
    if (aggregate.count) {
      snapshot = await getCountFromServer(q)
      console.debug(`getCollectionAggregate('${collection}', configs..) get count`, snapshot.data(), { configs })
    } else {
      snapshot = await getAggregateFromServer(q, aggregate.spec)
      console.debug(`getCollectionAggregate('${collection}', configs..) get aggregate spec`, snapshot.data(), { configs })
    }

    return snapshot.data()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getDocument(collection: Collection, documentID: string): Promise<any> {
    return this.afs
      .collection(collection)
      .doc(documentID)
      .ref.get()
      .then((doc) => {
        if (!doc.exists) {
          throw new Error('Document does not exist');
        }

        const data = doc.data()
        data['id'] = doc.id;
        return data;
      });
  }

  public streamDocument<T>(collection: string, documentID: string): Observable<T> {
    return this.afs.collection<T>(collection).doc(documentID).valueChanges({ idField: 'id' });
  }

  private _join(conditions: Condition[]): QueryFilterConstraint[] {
    const constraints: QueryFilterConstraint[] = []

    // Apply where conditions
    conditions.forEach(condition => {
      constraints.push(where(condition.field, condition.operator, condition.value))
    });

    return constraints
  }

  private _limit(paginate?: PaginateBy): number | undefined {
    if (paginate?.limit !== undefined && paginate.limit !== PageBy.NoLimitSize) {
      return Math.min(paginate.limit, PageBy.MaximumSize);
    }
    return undefined;
  }

  public updateDocument<T>(collection: Collection, documentID: string, updateData: Partial<T>, returnDocument?: boolean): Promise<T | null> {
    return this.afs
      .collection(collection)
      .doc(documentID)
      .update(updateData)
      .then(() => {
        if (returnDocument) {
          // Fetch the updated document
          return this.afs.collection(collection).doc(documentID).ref.get()
            .then(doc => {
              if (doc.exists) {
                const data = doc.data();
                if (data) {
                  (data as any)['id'] = doc.id;
                  return data as T;
                } else {
                  throw new Error('No data found in document');
                }
              } else {
                throw new Error('Document does not exist');
              }
            });
        } else {
          // Return null if fetching the document is not required
          return Promise.resolve(null);
        }
      })
      .catch(error => {
        console.error('Error updating document:', error);
        throw error;
      });
  }

  public createDocument<T>(collection: Collection, documentData: T, subCollection?: SubCollection): Promise<T> {
    let collectionReference: AngularFirestoreCollection<T>;

    if (subCollection) {
      collectionReference = this.afs.collection<T>(`${collection}/${subCollection}`);
    } else {
      collectionReference = this.afs.collection<T>(collection);
    }

    return collectionReference
      .add(documentData)
      .then(docRef => {
        const data = { ...documentData, id: docRef.id };
        return data;
      })
      .catch(error => {
        console.error('Error creating document:', error);
        throw error;
      });
  }

  public createCollectionMap<T extends { id?: string }>(res: T[]): { [key: string]: T } {
    const collectionMap: { [key: string]: T } = {};
    res.forEach((document: T) => {
      collectionMap[document.id] = document;
    });
    return collectionMap;
  }

  public batchWrite(operations: BatchOperation[]): Promise<void> {
    const BATCH_LIMIT = 500; // Firestore's batch write limit
    const batches = this.splitIntoBatches(operations, BATCH_LIMIT);

    return Promise.all(
      batches.map(async batchOperations => {
        const batch = this.afs.firestore.batch();

        for (const { collection, documentID, parentDocOfSubCollectionID, subCollection, operationType, documentData } of batchOperations) {

          const collectionPath = parentDocOfSubCollectionID && subCollection
            ? `${collection}/${parentDocOfSubCollectionID}/${subCollection}`
            : collection;

          const collectionRef = this.afs.firestore.collection(collectionPath);

          switch (operationType) {
            case 'set': {
              const docRef = documentID ? collectionRef.doc(documentID) : collectionRef.doc();
              console.log(docRef)
              if (!docRef) {
                throw new Error("Failed",)
              }
              batch.set(docRef, documentData || {}, { merge: true });
              break;
            }

            case 'update': {
              if (!documentID) {
                throw new Error('Document ID is required for update operations.');
              }
              const docRef = collectionRef.doc(documentID);
              batch.update(docRef, documentData || {});
              break;
            }

            case 'delete': {
              if (!documentID) {
                throw new Error('Document ID is required for delete operations.');
              }
              const docRef = collectionRef.doc(documentID);
              batch.delete(docRef);
              break;
            }

            default:
              throw new Error(`Unsupported operation type: ${operationType}`);
          }
        }

        return batch.commit();
      })
    )
      .then(() => {
        console.log('All batches committed successfully.');
      })
      .catch(error => {
        console.error('Error committing batches:', error);
        throw error;
      });
  }

  /**
   * Splits operations into smaller batches to adhere to Firestore's batch write limit.
   * @param operations - The array of operations to split into batches.
   * @param batchLimit - The Firestore batch operation limit.
   * @returns An array of operation batches.
   */
  private splitIntoBatches<T>(operations: T[], batchLimit: number): T[][] {
    const batches: T[][] = [];
    for (let i = 0; i < operations.length; i += batchLimit) {
      batches.push(operations.slice(i, i + batchLimit));
    }
    return batches;
  }

  public stripDocumentMetadata<T extends object>(doc: T | null): Partial<T> {
    if (!doc) return {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    const { metadata, timestamp, createdAt, updatedAt, ...cleanedDoc } = doc as any;
    return cleanedDoc;
  }

}
